import { Checkbox } from "@gromia/components/input/Checkbox";
import { Textfield } from "@gromia/components/input/Textfield";
import * as s from "@gromia/components/theme/t.module.css";
import { Button } from "@gromia/components/ui/Button";
import * as f from "@gromia/formTypes";
import strings from "@gromia/strings/leadForm";
import { getTrackingData } from "@gromia/tracking";
import {
  handleApiResponseLowLevel,
  mapFormErrors,
  post,
  put,
} from "@gromia/utils/network";
import { useForm } from "@gromia/utils/useForm";
import { FormStepProps } from "@gromia/utils/useStepData";
import { useString } from "@gromia/utils/useString";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import { mapLeft } from "fp-ts/lib/TaskEither";
import * as t from "io-ts";
import React from "react";

export const Form = t.interface({
  interest: t.keyof({ sell: 1, rent: 2, referrer: 3, switch: 4 }),
  name: f.string,
  surname: f.string,
  email: f.email,
  phone: f.phone,
  tos: f.flag,
});

export type FormT = t.TypeOf<typeof Form>;

export type Data = FormT & {
  valuationData?: string;
  valuationDataHumanReadable?: string;
  data?: string;
  refslug?: string;
  owner?: string;
  geocode?: string;
  tags?: string[];
};

export function createLead(
  data: Data,
  campaign: string = "landlord",
  funnel?: string,
) {
  const td = getTrackingData();

  return pipe(
    post("/api/v2/leads.json", {
      data: {
        ...data,
        ...td,
        referrer: funnel,
        campaign,
      },
      middleware: handleApiResponseLowLevel,
    }),
    mapLeft(mapFormErrors),
  );
}

export function updateLead(
  id: string,
  data: Partial<Data>,
  campaign: string = "landlord",
) {
  return pipe(
    put(`/api/v2/leads/${id}.json`, {
      data: { ...data, campaign },
      middleware: handleApiResponseLowLevel,
    }),
    mapLeft(mapFormErrors),
  );
}

type Props = {
  interest: FormT["interest"];
  city?: string;
  ctaLabel?: React.ReactNode;
} & FormStepProps<typeof Form>;

export function LeadForm(props: Props) {
  const { tx, raw } = useString("leadForm", strings);
  const form = useForm("leadForm", Form, {
    ...props,
    initialValues: {
      ...props.initialValues,
      interest: props.interest,
    },
  });

  return (
    <form {...form.formProps()}>
      {form.submitted ? props.afterSubmit() : null}
      <div className={cx(s.row)}>
        <div className={cx(s.colLg6)}>
          <Textfield
            type="text"
            name="name"
            label={tx("name")}
            placeholder={raw("placeholerName").raw}
            {...form.props("name")}
          />
        </div>
        <div className={cx(s.colLg6)}>
          <Textfield
            type="text"
            name="surname"
            label={tx("surname")}
            placeholder={raw("placeholerSurname").raw}
            {...form.props("surname")}
          />
        </div>
      </div>
      <div className={cx(s.row)}>
        <div className={cx(s.colLg6)}>
          <Textfield
            type="email"
            name="email"
            label={tx("email")}
            placeholder={raw("placeholerEmail").raw}
            {...form.props("email")}
          />
        </div>
        <div className={cx(s.colLg6)}>
          <Textfield
            type="tel"
            name="phone"
            label={tx("phone")}
            placeholder={raw("placeholerPhone").raw}
            prependNode="+39"
            {...form.props("phone")}
          />
        </div>
      </div>
      <div className={cx(s.pt3)}>
        <Checkbox name="tos" label={tx("tos")} {...form.propsChecked("tos")} />
      </div>
      <div className={cx(s.spaceTop1, s.textCenter)}>
        <Button type="submit" color="btnCta" {...form.submitButtonProps()}>
          {props.ctaLabel ? props.ctaLabel : tx("cta")}
        </Button>
      </div>
    </form>
  );
}
